import { Heading, SpinnerContainer, Text, theme } from '@alefeducation/ui-elements-library-core'
import { ArrowLeft } from '@alefeducation/ui-elements-library-icons'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import JsonView from '@uiw/react-json-view'
import { DateBlock } from '../../components/LogBlock'
import getAdditionalDetails from '../../xhr/getAdditionalDetails'

const PanelWrapper = styled.div`
  overflow-y: scroll;
  background-color: ${theme.colors.primary[0]};
  width: ${props => (props.isCentralisedScreen ? '378px' : '100%')};
  position: fixed;
  top: 0;
  ${({ isCentralisedScreen }) => (isCentralisedScreen ? 'right: 0;' : 'left: 0;')}
  height: 100%;
  z-index: 9999;

  .headerWrapper {
    background-color: ${theme.colors.interactive[30]};

    .closeWrapper {
      display: flex;
      padding: 16px;
      align-items: flex-start;
      gap: 10px;

      > svg {
        cursor: pointer;
      }
    }

    .headingWrapper {
      display: flex;
      padding: 8px 24px 24px 24px;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;
    }
  }

  .bodyWrapper {
    padding: 24px;
    display: flex;
    gap: 20px;
    flex-direction: column;
    position: relative;

    > #loadingWrapper {
      position: absolute;
      z-index: 99999;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      background-color: ${theme.colors.backgroundLight};
      display: flex;
      justify-content: center;
      align-items: center;
    }

    > div {
      display: flex;
      gap: 5px;
      flex-direction: column;
    }
  }
`

export enum MaterialType {
  IP = 'InstructionalPlan',
  COURSE = 'COURSE',
  CORE_COURSE = 'CORE',
  PATHWAY = 'PATHWAY'
}

type Value =  IdAndType[] | string[] | metadataDetails[]

export const AdditionalPanel = ({
                                  message,
                                  oldValue,
                                  newValue,
                                  auditDate,
                                  additionalDetails,
                                  onClosePanel,
                                  fieldDisplayType
                                }: FieldUpdateLog & {
  auditDate: AuditLogObject['auditDate'];
  onClosePanel: () => void
}) => {
  const [details, setDetails] = useState<{
    type: string | null
    oldValue: { id: string; name: string }[]
    newValue: { id: string; name: string }[]
  }>({
    type: additionalDetails && additionalDetails.fieldName,
    oldValue: [],
    newValue: []
  })

  const isCentralisedScreen = !window.location.href.includes('/sidePanel')

  const [loading, setLoading] = useState<boolean>(true)

  const getDetails = async ({ fieldName, oldValue, newValue }) => {
    setLoading(true)
    const fetchedDetails = await getAdditionalDetails({ fieldName, oldValue, newValue })
    setDetails(fetchedDetails)
    setLoading(false)
  }

  const renderValues = ({ values }: { values: { name: string }[] }) => {
    return values && values.length
      ? values.map(value => (
        <div>
          <Text variant="body-large" weight="light" color="primary.100" key={value.name}>
            <div
              dangerouslySetInnerHTML={{
                __html: value.name
              }}
            />
          </Text>
        </div>
        ))
      : '---'
  }

  useEffect(() => {
    if (additionalDetails && additionalDetails.fetchDataExternally && (fieldDisplayType === 'JSON' || fieldDisplayType === 'TEXT') ) {
      getDetails({ fieldName: additionalDetails.fieldName, oldValue: oldValue, newValue: newValue })
      return
    }
    else {
      setDetails({
        type: additionalDetails && additionalDetails.fieldName,
        oldValue: Array.isArray(oldValue) ? oldValue.map(value => ({ id: value, name: value })) : [{ id: oldValue, name: oldValue }],
        newValue: Array.isArray(newValue) ? newValue.map(value => ({ id: value, name: value })) : [{ id: newValue, name: newValue }]
      })
    }

    setLoading(false)
  }, [additionalDetails && additionalDetails.fetchDataExternally, additionalDetails && additionalDetails.fieldName, oldValue, newValue])

  const renderJSON = (oldValue: Value, newValue: Value) => (
    <div>
      <div>
        <Heading variant="heading-small" weight="light" color="primary.100">
          Before
        </Heading>
        <JsonView value={oldValue} collapsed={2} />
      </div>
      <div>
        <Heading variant="heading-small" weight="light" color="primary.100">
          After:
        </Heading>
        <JsonView value={newValue} collapsed={2} />
      </div>
    </div>
  )

  return (
    <PanelWrapper data-testid="additional-panel" isCentralisedScreen={isCentralisedScreen}>
      <div className="headerWrapper">
        <div className="closeWrapper">
          <ArrowLeft data-testid="close-additional-panel" color="interactive.140" onClick={onClosePanel} width={14} />
        </div>
        <div className="headingWrapper" data-testid="panel-header-heading">
          <Heading variant="heading-xl" weight="bold" color="interactive.160">
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: message
              }}
            />
          </Heading>
        </div>
      </div>
      { ((additionalDetails === null) || (additionalDetails && !additionalDetails.fetchDataExternally)) && fieldDisplayType === 'JSON'? renderJSON(oldValue, newValue) : (
        <div className="bodyWrapper">
          {loading ? (
            <div id="loadingWrapper">
              <SpinnerContainer />
            </div>
          ) : <></>}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Heading variant="heading-small" weight="light" color="primary.100">
                Before:
              </Heading>
              <DateBlock date={auditDate} />
            </div>
            {renderValues({ values: details.oldValue })}
          </div>
          <div>
            <Heading variant="heading-small" weight="light" color="primary.100">
              After:
            </Heading>
            {renderValues({ values: details.newValue })}
          </div>
        </div>
      )}
    </PanelWrapper>
  )
}

export default AdditionalPanel
